import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowLeft, Pencil } from 'lucide-react';

const ProductDetailPage = ({ sess = { uid: '', role: 'user' } }) => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch('/chatbot/api/v1.0/catalog/get', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: sess.uid, productId }),
        });
        const result = await response.json();
        if (response.ok && result?.data) {
          setProduct(result.data);
        } else {
          setError(result.message || 'Product not found');
        }
      } catch (err) {
        setError('Error loading product');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [sess.uid, productId]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button variant="contained" onClick={() => navigate('/catalog')}>
          Back to Catalog
        </Button>
      </Stack>
    );
  }

  if (!product) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ p: 3 }}>
        <Typography>Product not found</Typography>
        <Button variant="contained" onClick={() => navigate('/catalog')}>
          Back to Catalog
        </Button>
      </Stack>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="outlined" startIcon={<ArrowLeft size={16} />} onClick={() => navigate('/catalog')}>
            Back
          </Button>
          <Typography variant="h5">Product Details</Typography>
        </Stack>
        <Button variant="outlined" startIcon={<Pencil size={16} />} onClick={() => navigate(`/catalog/edit/${productId}`)}>
          Edit
        </Button>
      </Stack>

      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Stack spacing={1}>
                <Typography variant="h6">{product.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {product.description || 'No description'}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={product.category || 'Uncategorized'} size="small" />
                  <Chip label={`Stock: ${product.stock || 0}`} size="small" color="info" />
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={1}>
                <Typography variant="h6" color="primary">
                  ₹{product.price}
                </Typography>
                {product.status && (
                  <Chip
                    label={product.status}
                    color={product.status === 'active' ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Created: {product.created_at ? new Date(product.created_at).toLocaleString() : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Updated: {product.updated_at ? new Date(product.updated_at).toLocaleString() : '-'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductDetailPage;
